import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase, supabaseConfigured } from "../lib/supabase";

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!supabase) {
      setSession(null);
      return undefined;
    }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    async function load() {
      const userId = session.user.id;
      const [{ data: memberships, error: memberError }, { data: permissions }] = await Promise.all([
        supabase.from("organization_members").select("id, organization_id, role_id, organizations(name, slug, status, welcome_message, primary_color, support_email), roles(key, name)").eq("user_id", userId).eq("status", "active"),
        supabase.from("role_permissions").select("role_id, permission_key"),
      ]);
      if (cancelled) return;
      if (memberError) {
        setError(memberError.message);
        return;
      }
      const membership = memberships?.[0] || null;
      const keys = new Set((permissions || []).filter((row) => row.role_id === membership?.role_id).map((row) => row.permission_key));
      setProfile({ membership, permissions: keys, organization: membership?.organizations || null, role: membership?.roles || null });
    }
    load();
    return () => { cancelled = true; };
  }, [session]);

  const value = useMemo(() => ({
    configured: supabaseConfigured,
    session,
    profile,
    error,
    can: (key) => Boolean(profile?.permissions?.has(key)),
    orgId: profile?.membership?.organization_id || null,
    signOut: () => supabase?.auth.signOut(),
  }), [session, profile, error]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}
